/**
 * Port of C# GeneratePreprocessMetadata.cs - Slot Processing
 * Parses slotted templates within preprocessed templates
 */

import { CommonUtil } from '../../common/commonUtil.js';
import {
    SlottedTemplate,
    SlotPlaceholder,
    TemplatePlaceholder
} from '../../model/index.js';
import type { PreprocessedTemplate } from '../../model/index.js';

// #region Slot Processing (Private)

/**
 * IndexOf-based version: Parses slotted templates in the content and adds them to the preprocessed template
 */
export function parseSlottedTemplates(content: string, appSite: string, template: PreprocessedTemplate): void {
    parseSlottedTemplatesInto(content, appSite, template.slottedTemplates);
}

/** Scans content for {{#Name}}/{{!#Name}} … {{/Name}} blocks and appends each parsed slotted template to target. */
export function parseSlottedTemplatesInto(content: string, appSite: string, target: SlottedTemplate[]): void {
    let searchPos = 0;

    while (searchPos < content.length) {
        // Look for opening tag {{# or {{!#
        let openStart = content.indexOf('{{#', searchPos);
        const openStartSkip = content.indexOf('{{!#', searchPos);

        // Use the earlier match
        let isSkipMarked = false;
        if (openStartSkip !== -1 && (openStart === -1 || openStartSkip < openStart)) {
            openStart = openStartSkip;
            isSkipMarked = true;
        }

        if (openStart === -1) break;

        // Calculate offset based on whether skip marker is present
        const nameOffset = isSkipMarked ? 4 : 3;  // "{{!#" or "{{#"

        // Find the end of the template name
        const openEnd = content.indexOf('}}', openStart + nameOffset);
        if (openEnd === -1) break;

        // Extract template name (without ! marker)
        const templateName = content.substring(openStart + nameOffset, openEnd).trim();
        if (!templateName || !CommonUtil.isAlphaNumeric(templateName)) {
            searchPos = openStart + 1;
            continue;
        }

        // Look for corresponding closing tag (always use templateName without !)
        const closeTag = '{{/' + templateName + '}}';
        const openTag = (isSkipMarked ? '{{!#' : '{{#') + templateName + '}}';
        const closeStart = CommonUtil.findMatchingCloseTag(content, openEnd + 2, openTag, closeTag);
        if (closeStart === -1) {
            searchPos = openStart + 1;
            continue;
        }

        // Extract inner content
        const innerStart = openEnd + 2;
        const innerContent = content.substring(innerStart, closeStart);
        const fullMatch = content.substring(openStart, closeStart + closeTag.length);

        // Create slotted template structure - include ! in Name if skip marked
        const slottedTemplate = new SlottedTemplate();
        slottedTemplate.name = isSkipMarked ? '!' + templateName : templateName;
        slottedTemplate.startIndex = openStart;
        slottedTemplate.endIndex = closeStart + closeTag.length;
        slottedTemplate.fullMatch = fullMatch;
        slottedTemplate.innerContent = innerContent;
        slottedTemplate.templateKey = templateName.toLowerCase(); // Simple template name since appSite is passed as parameter

        // Parse slots within this slotted template using IndexOf
        parseSlots(innerContent, slottedTemplate, appSite);

        target.push(slottedTemplate);
        searchPos = closeStart + closeTag.length;
    }
}

/**
 * IndexOf-based version: Parses slots within a slotted template
 */
export function parseSlots(innerContent: string, slottedTemplate: SlottedTemplate, appSite: string): void {
    let searchPos = 0;

    while (searchPos < innerContent.length) {
        // Look for slot start {{@HTMLPLACEHOLDER
        const slotStart = innerContent.indexOf('{{@HTMLPLACEHOLDER', searchPos);
        if (slotStart === -1) break;

        // Find the number (if any) and closing }}
        const afterPlaceholder = slotStart + 18; // Length of "{{@HTMLPLACEHOLDER"
        let slotNum = '';
        let pos = afterPlaceholder;

        // Extract slot number
        while (pos < innerContent.length && /\d/.test(innerContent[pos])) {
            slotNum += innerContent[pos];
            pos++;
        }

        // Check for closing }}
        if (pos + 1 >= innerContent.length || innerContent.substring(pos, pos + 2) !== '}}') {
            searchPos = slotStart + 1;
            continue;
        }

        const slotOpenEnd = pos + 2;

        // Find matching closing tag
        const closeTag = !slotNum ? '{{/HTMLPLACEHOLDER}}' : `{{/HTMLPLACEHOLDER${slotNum}}}`;
        const openTag = !slotNum ? '{{@HTMLPLACEHOLDER}}' : `{{@HTMLPLACEHOLDER${slotNum}}}`;

        const closeStart = CommonUtil.findMatchingCloseTag(innerContent, slotOpenEnd, openTag, closeTag);
        if (closeStart === -1) {
            searchPos = slotStart + 1;
            continue;
        }

        // Extract slot content
        const slotContent = innerContent.substring(slotOpenEnd, closeStart);

        // Generate slot key
        const slotKey = !slotNum ? '{{$HTMLPLACEHOLDER}}' : `{{$HTMLPLACEHOLDER${slotNum}}}`;

        // Create slot structure
        const slot = new SlotPlaceholder();
        slot.number = slotNum;
        slot.startIndex = slotStart;
        slot.endIndex = closeStart + closeTag.length;
        slot.content = slotContent;
        slot.slotKey = slotKey;
        slot.openTag = openTag;
        slot.closeTag = closeTag;

        // Parse nested templates within the slot content (JSON not available during parsing)
        parseNestedTemplatesInSlot(slot, appSite);

        slottedTemplate.slots.push(slot);
        searchPos = closeStart + closeTag.length;
    }
}

/**
 * Parses nested templates within slot content (simple version without recursion)
 */
export function parseNestedTemplatesInSlot(slot: SlotPlaceholder, appSite: string): void {
    if (!slot.content) {
        return;
    }

    const content = slot.content;

    // Parse simple placeholders like {{ComponentName}} or {{!ComponentName}} using IndexOf
    let searchPos = 0;
    while (searchPos < content.length) {
        const openStart = content.indexOf('{{', searchPos);
        if (openStart === -1) break;

        // Skip if it's a special placeholder
        if (openStart + 2 < content.length &&
            (content[openStart + 2] === '#' || content[openStart + 2] === '@' ||
             content[openStart + 2] === '$' || content[openStart + 2] === '/')) {
            searchPos = openStart + 2;
            continue;
        }

        // Check for skip marker (!)
        let isSkipMarked = false;
        let nameOffset = 2; // Default: "{{"
        if (openStart + 2 < content.length && content[openStart + 2] === '!') {
            isSkipMarked = true;
            nameOffset = 3; // "{{!"
        }

        const closeStart = content.indexOf('}}', openStart + nameOffset);
        if (closeStart === -1) break;

        const templateName = content.substring(openStart + nameOffset, closeStart).trim();
        if (templateName && CommonUtil.isAlphaNumeric(templateName)) {
            const templateKey = templateName.toLowerCase();

            const placeholder = new TemplatePlaceholder();
            placeholder.name = isSkipMarked ? '!' + templateName : templateName;
            placeholder.startIndex = openStart;
            placeholder.endIndex = closeStart + 2;
            placeholder.fullMatch = content.substring(openStart, closeStart + 2);
            placeholder.templateKey = templateKey;

            slot.nestedPlaceholders.push(placeholder);
        }

        searchPos = closeStart + 2;
    }

    // Parse slotted templates like {{#TemplateName}} or {{!#TemplateName}} ... {{/TemplateName}} using IndexOf
    parseSlottedTemplatesInto(content, appSite, slot.nestedSlottedTemplates);
}

// #endregion
